import React, { useEffect, useState, useContext } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../../contexts/AuthContext/AuthContext";
import PasswordInput from "../../common/PasswordInput";
import "../Auth.css";

// Players can play before confirming their email; this nudges them to do it
// so password and username reminders can reach them.
const ConfirmEmailNote = ({ email, serverUrl, token }) => {
  const [state, setState] = useState("idle"); // idle | sending | sent | error
  const resend = async () => {
    setState("sending");
    try {
      await axios.post(
        `${serverUrl}/api/verify-email/resend/`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setState("sent");
    } catch (err) {
      setState("error");
    }
  };
  return (
    <div className="auth-message auth-message--info" role="status">
      <p>
        Please confirm your email address, <strong>{email}</strong>. We sent you a
        link when you signed up.
      </p>
      {state === "sent" ? (
        <p>
          Sent. If it's not in your inbox within a few minutes, look in your junk or
          spam folder. It's from support@mendgrove.com.
        </p>
      ) : (
        <button
          type="button"
          className="auth-link-button"
          onClick={resend}
          disabled={state === "sending"}
        >
          {state === "sending" ? "Sending..." : "Send the link again"}
        </button>
      )}
      {state === "error" && (
        <p className="auth-message--error">
          We couldn't send it just now. Please try again later.
        </p>
      )}
    </div>
  );
};

const SettingsPage = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const { user, authTokens, logout, updateUser, isPatient } = useContext(AuthContext);
  // An account a provider set up starts with a temporary password. They have
  // just signed in with it, so it isn't asked for again.
  const firstSignIn = !!user?.must_change_password;
  const navigate = useNavigate();
  const access = authTokens?.access;

  // The stored profile can be out of date (e.g. the email was confirmed on
  // another device), so refresh it here.
  useEffect(() => {
    if (!access || !updateUser) return;
    let alive = true;
    axios
      .get(`${SERVER_URL}/api/current-user/`, {
        headers: { Authorization: `Bearer ${access}` },
      })
      .then((res) => {
        if (alive && res?.data) updateUser(res.data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access, SERVER_URL]);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsLoading(true);

    if (!authTokens?.access) {
      setError("You are not authenticated. Please sign in.");
      navigate("/signin");
      setIsLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New password and confirmation do not match.");
      setIsLoading(false);
      return;
    }

    try {
      // An expired access token is renewed and the request replayed by the
      // axios interceptor (services/authSession.js).
      const headers = { Authorization: `Bearer ${authTokens.access}` };
      if (firstSignIn) {
        await axios.post(
          `${SERVER_URL}/api/account/first-password/`,
          { new_password: newPassword, re_new_password: confirmPassword },
          { headers }
        );
      } else {
        await axios.post(
          `${SERVER_URL}/api/auth/users/set_password/`,
          {
            current_password: currentPassword,
            new_password: newPassword,
          },
          { headers }
        );
      }
      setSuccess("Password changed successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      if (firstSignIn) {
        updateUser({ must_change_password: false });
        // Next step of setup: approve sharing with the provider who invited them.
        navigate("/settings/care-team");
      }
    } catch (err) {
      if (err.response?.status === 401) {
        // The session couldn't be renewed: it has been signed out.
        logout();
        navigate("/signin");
        return;
      }
      setError(
        err.response?.data?.current_password?.[0] ||
          err.response?.data?.new_password?.[0] ||
          err.response?.data?.re_new_password?.[0] ||
          err.response?.data?.detail ||
          "Failed to change password. Please check your current password."
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!user || !authTokens) {
    navigate("/signin");
    return null;
  }

  return (
    <div className="auth-page">
      <Helmet>
        <title>Settings – Mend Grove</title>
        <meta
          name="description"
          content="Change your Mend Grove account password securely. Update your credentials and keep your account safe."
        />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://mendgrove.com/settings" />
        <meta property="og:title" content="Settings – Mend Grove" />
        <meta
          property="og:description"
          content="Change your Mend Grove account password securely. Update your credentials and keep your account safe."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mendgrove.com/settings" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Settings – Mend Grove" />
        <meta
          name="twitter:description"
          content="Change your Mend Grove account password securely. Update your credentials and keep your account safe."
        />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Mend Grove",
              "url": "https://mendgrove.com/",
              "description": "Mend Grove offers cognitive training and brain exercises to improve memory, attention, and mental agility."
            }
          `}
        </script>
      </Helmet>
      <div className="auth-card">
        {firstSignIn && (
          <p className="auth-message auth-message--success" role="status">
            Welcome to MendGrove. Please choose a new password to replace the
            temporary one you were given.
          </p>
        )}
        {isPatient && user.has_email && user.email_verified === false && (
          <ConfirmEmailNote email={user.email} serverUrl={SERVER_URL} token={access} />
        )}
        <h2 className="auth-title">
          {firstSignIn ? "Choose a Password" : "Change Password"}
        </h2>
        <form onSubmit={handlePasswordChange}>
          {!firstSignIn && (
            <div className="auth-field">
              <label
                htmlFor="currentPassword"
                className="auth-label"
              >
                Current Password
              </label>
              <PasswordInput
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoFocus
                autoComplete="current-password"
              />
            </div>
          )}
          <div className="auth-field">
            <label
              htmlFor="newPassword"
              className="auth-label"
            >
              New Password
            </label>
            <PasswordInput
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              autoFocus={firstSignIn}
              autoComplete="new-password"
            />
          </div>
          <div className="auth-field">
            <label
              htmlFor="confirmPassword"
              className="auth-label"
            >
              Confirm New Password
            </label>
            <PasswordInput
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
          </div>
          {error && (
            <p className="auth-message auth-message--error" aria-live="polite">
              {error}
            </p>
          )}
          {success && (
            <p
              className="auth-message auth-message--success"
              aria-live="polite"
            >
              {success}
            </p>
          )}
          <button type="submit" className="auth-submit" disabled={isLoading}>
            {isLoading
              ? "Saving..."
              : firstSignIn
                ? "Save Password"
                : "Change Password"}
          </button>
        </form>
        <div className="auth-footer">
          {isPatient && (
            <button
              onClick={() => navigate("/settings/care-team")}
              className="auth-link-button"
            >
              My care team
            </button>
          )}
          <button
            onClick={() => navigate("/home")}
            className="auth-link-button"
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
